import { Button, Card, Empty, List, Space, Tag, Typography } from "antd";
import { FilePdfOutlined, EyeOutlined } from "@ant-design/icons";
import { useState } from "react";
import { ApiService } from "api/api.service";

type PandaDocDocument = {
  name: string;
  doc_id: string;
  status: string;
};

interface PandaDocDocumentsProps {
  appkey: string;
  pandadoc?: PandaDocDocument[];
}

const getStatusLabel = (status: string) => {
  switch (status) {
    case "document.sent":
      return "Sent";
    case "document.draft":
      return "Draft";
    case "document.completed":
      return "Completed";
    default:
      return status
        .replace("document.", "")
        .replace(/_/g, " ")
        .replace(/\b\w/g, (char) => char.toUpperCase());
  }
};

const getStatusColor = (status: string) => {
  switch (status) {
    case "document.completed":
      return "success";
    case "document.sent":
      return "processing";
    case "document.draft":
      return "default";
    default:
      return "default";
  }
};

const escapeHtml = (str: string) =>
  str.replace(
    /[&<>"']/g,
    (c) =>
      ({
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        '"': "&quot;",
        "'": "&#39;",
      })[c] || c,
  );

const LOADING_HTML = (docName: string) => `
  <div style="
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 16px;
    height: 100vh;
    margin: 0;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    background: #fafafa;
  ">
    <div style="
      width: 40px;
      height: 40px;
      border: 3px solid #e6e6e6;
      border-top-color: #1677ff;
      border-radius: 50%;
      animation: spin 0.8s linear infinite;
    "></div>
    <div style="color: #333; font-size: 15px; font-weight: 500;">
      Preparing document
    </div>
    <div style="color: #999; font-size: 13px; max-width: 320px; text-align: center;">
      ${escapeHtml(docName)}
    </div>
    <style>
      @keyframes spin { to { transform: rotate(360deg); } }
    </style>
  </div>
`;

const ERROR_HTML = (docName: string) => `
  <div style="
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;
    height: 100vh;
    margin: 0;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    background: #fafafa;
  ">
    <div style="font-size: 32px;">⚠️</div>
    <div style="color: #333; font-size: 15px; font-weight: 500;">
      Couldn't load document
    </div>
    <div style="color: #999; font-size: 13px; max-width: 320px; text-align: center;">
      ${escapeHtml(docName)} failed to load. You can close this tab and try again.
    </div>
  </div>
`;

export const GetPandaDocFile = ({ appkey, pandadoc }: PandaDocDocumentsProps) => {
  const getPandaDocPDF = ApiService.upstream.useGetPandaDocPDF();
  const [loadingDocId, setLoadingDocId] = useState<string>();

  const handleView = async (doc: PandaDocDocument) => {
    const newTab = window.open("", "_blank");

    if (!newTab) {
      return;
    }

    setLoadingDocId(doc.doc_id);
    newTab.document.title = doc.name;
    newTab.document.body.innerHTML = LOADING_HTML(doc.name);

    try {
      const pdfBlob = await getPandaDocPDF.mutateAsync({
        appkey,
        doc_id: doc.doc_id,
      });

      const blob = new Blob([pdfBlob], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);

      newTab.location.href = url;

      setTimeout(
        () => {
          URL.revokeObjectURL(url);
        },
        5 * 60 * 1000,
      );
    } catch (error) {
      newTab.document.body.innerHTML = ERROR_HTML(doc.name);
      throw error;
    } finally {
      setLoadingDocId(undefined);
    }
  };

  if (!pandadoc?.length) {
    // return <Empty description="No PandaDoc documents found." />;
    return;
  }

  return (
    <Card title="PandaDoc Documents">
      <List
        dataSource={pandadoc}
        renderItem={(doc) => {
          const isLoading = loadingDocId === doc.doc_id;
          const isDisabled = !!loadingDocId && !isLoading;

          return (
            <List.Item
              actions={[
                <Button
                  key="view"
                  type="primary"
                  icon={<EyeOutlined />}
                  loading={isLoading}
                  disabled={isDisabled}
                  onClick={() => handleView(doc)}
                >
                  {isLoading ? "Preparing..." : "View"}
                </Button>,
              ]}
            >
              <List.Item.Meta
                avatar={<FilePdfOutlined />}
                title={doc.name}
                description={
                  <Space>
                    <Tag color={getStatusColor(doc.status)}>{getStatusLabel(doc.status)}</Tag>
                    {isLoading && <Typography.Text type="secondary">Opening in new tab...</Typography.Text>}
                  </Space>
                }
              />
            </List.Item>
          );
        }}
      />
    </Card>
  );
};
